import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useId,
  forwardRef,
} from 'react';
import { createPortal } from 'react-dom';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { useOutsideClick, useMobileScrollLock } from '@/common/hooks';

const PORTAL_ID = 'crm-datetime-picker-portal';
const DATE_FORMAT = 'd MMMM yyyy';
const DATETIME_FORMAT = 'd MMMM yyyy, h:mm aa';
const TIME_DROPDOWN_MAX_H = 220;
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const POPPER_PROPS = {
  strategy: 'fixed',
  modifiers: [
    {
      name: 'preventOverflow',
      options: {
        padding: 16,
        boundary: 'viewport',
        altAxis: true,
        tether: false,
      },
    },
    {
      name: 'flip',
      options: {
        fallbackPlacements: [
          'top-start',
          'top',
          'bottom-start',
          'bottom',
          'auto',
        ],
        padding: 16,
      },
    },
    {
      name: 'offset',
      options: {
        offset: [0, 6],
      },
    },
  ],
};

function ensurePickerPortal() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PORTAL_ID)) return;
  const el = document.createElement('div');
  el.id = PORTAL_ID;
  document.body.appendChild(el);
}

function isPickerExemptTarget(target) {
  if (!(target instanceof Element)) return false;
  return Boolean(
    target.closest('[data-portal-dropdown="true"]') ||
    target.closest(`#${PORTAL_ID}`) ||
    target.closest('.react-datepicker-popper') ||
    target.closest('.react-datepicker'),
  );
}

const DateTimeTextInput = forwardRef(function DateTimeTextInput(
  { displayValue, className, ...props },
  ref,
) {
  return (
    <input
      {...props}
      ref={ref}
      type="text"
      readOnly
      value={displayValue}
      className={className}
    />
  );
});

export function TimeSelectDropdown({
  value,
  options,
  onChange,
  width = 'w-16',
  disabled,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({
    position: 'fixed',
    top: -9999,
    left: -9999,
  });
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  useMobileScrollLock(isOpen);

  useOutsideClick(
    [containerRef, dropdownRef],
    () => {
      setIsOpen(false);
    },
    isOpen,
  );

  useLayoutEffect(() => {
    const updatePosition = () => {
      if (!isOpen || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp =
        spaceBelow < TIME_DROPDOWN_MAX_H + 8 && rect.top > spaceBelow;

      setDropdownStyle({
        position: 'fixed',
        left: rect.left,
        width: rect.width,
        zIndex: 1000000,
        ...(openUp
          ? { bottom: window.innerHeight - rect.top + 4 }
          : { top: rect.bottom + 4 }),
      });
    };

    const onScroll = (event) => {
      if (
        dropdownRef.current &&
        (event.target === dropdownRef.current ||
          dropdownRef.current.contains(event.target))
      ) {
        return;
      }
      updatePosition();
    };

    updatePosition();
    if (isOpen) {
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', onScroll, true);
    }
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const selectedEl = dropdownRef.current.querySelector(
        '[data-selected="true"]',
      );
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'center' });
      }
    }
  }, [isOpen]);

  const selectedOpt = options.find(
    (opt) => String(opt.value) === String(value),
  );

  return (
    <div className={`relative ${width}`} ref={containerRef}>
      <button
        type="button"
        disabled={disabled}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full h-11 flex items-center justify-between px-3 rounded-xl bg-white border ${
          disabled
            ? 'opacity-60 cursor-not-allowed bg-gray-50 border-[#EBF4EA]'
            : isOpen
              ? 'border-[#50AB3D] ring-2 ring-[#50AB3D]/15 shadow-sm'
              : 'border-[#EBF4EA] hover:border-[#50AB3D] shadow-sm'
        } text-[#1F2241] text-sm font-semibold focus:outline-none transition-all`}
      >
        <span>{selectedOpt ? selectedOpt.label : value}</span>
        <svg
          className={`w-3.5 h-3.5 text-[#50AB3D] transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M19.5 8.25l-7.5 7.5-7.5-7.5"
          />
        </svg>
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            style={dropdownStyle}
            data-portal-dropdown="true"
            className="max-h-[220px] overflow-y-auto no-scrollbar bg-[#ffffff] border border-[#EBF4EA] rounded-xl shadow-xl py-1"
          >
            {options.map((opt) => {
              const isSelected = String(opt.value) === String(value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-selected={isSelected}
                  disabled={opt.disabled}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    if (opt.disabled) return;
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full h-7 px-2 flex items-center justify-center text-xs font-semibold transition-colors ${
                    opt.disabled
                      ? 'opacity-40 cursor-not-allowed text-[#868686]'
                      : isSelected
                        ? 'bg-[#EBF4EA] text-[#50AB3D]'
                        : 'text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D]'
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>,
          document.body,
        )}
    </div>
  );
}

export function YearSelectDropdown({ value, options, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  useMobileScrollLock(isOpen);

  useOutsideClick(
    [containerRef, dropdownRef],
    () => {
      setIsOpen(false);
    },
    isOpen,
  );

  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const selectedEl = dropdownRef.current.querySelector(
        '[data-selected="true"]',
      );
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'center' });
      }
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block" ref={containerRef}>
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="flex items-center justify-center bg-[#F5F8F5] hover:bg-[#EBF4EA] text-[#1F2241] hover:text-[#50AB3D] font-bold text-sm py-1 px-2.5 rounded-lg border border-[#EBF4EA] hover:border-[#50AB3D]/40 transition-all cursor-pointer"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span>{value}</span>
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          data-portal-dropdown="true"
          className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-max min-w-[82px] max-h-[168px] overflow-y-auto stage-column-scrollbar bg-white border border-[#EBF4EA] rounded-xl shadow-xl py-1 z-[999999]"
        >
          {options.map((yr) => {
            const isSelected = yr === value;
            return (
              <button
                key={yr}
                type="button"
                data-selected={isSelected}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange(yr);
                  setIsOpen(false);
                }}
                className={`w-full h-7 px-2 flex items-center justify-center text-xs font-bold transition-colors cursor-pointer ${
                  isSelected
                    ? 'bg-[#EBF4EA] text-[#50AB3D]'
                    : 'text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D]'
                }`}
              >
                {yr}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CommonDatePicker({
  selected,
  onChange,
  mode = 'datetime',
  allowPastDates = true,
  allowFutureDates = true,
  minDate = null,
  maxDate = null,
  disablePastTime = false,
  minuteStep = 1,
  placeholderText = 'Select date',
  dateFormat,
  disabled = false,
  className = '',
}) {
  const [placement, setPlacement] = useState('bottom-start');
  const [hour, setHour] = useState('12');
  const [minute, setMinute] = useState('00');
  const [ampm, setAmpm] = useState('PM');
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(selected);
  const [showTimePanel, setShowTimePanel] = useState(false);
  const instanceId = useId();
  const containerRef = useRef(null);

  useMobileScrollLock(isOpen);

  const calculatePlacement = () => {
    if (typeof window !== 'undefined' && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Calendar + Time Panel height is ~440px
      if (spaceBelow < 450 && spaceAbove > spaceBelow) {
        return 'top-start';
      }
    }
    return 'bottom-start';
  };

  const computedDateFormat =
    dateFormat || (mode === 'date' ? DATE_FORMAT : DATETIME_FORMAT);

  useEffect(() => {
    ensurePickerPortal();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setDraft(selected);
      setShowTimePanel(false);
    }
  }, [isOpen, selected]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const listener = (event) => {
      if (isPickerExemptTarget(event.target)) return;
      if (containerRef.current?.contains(event.target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', listener, true);
    document.addEventListener('touchstart', listener, true);
    return () => {
      document.removeEventListener('mousedown', listener, true);
      document.removeEventListener('touchstart', listener, true);
    };
  }, [isOpen]);

  useEffect(() => {
    function handleOtherDropdownOpened(e) {
      if (e.detail?.id !== instanceId) {
        setIsOpen(false);
      }
    }
    document.addEventListener('dropdown:opened', handleOtherDropdownOpened);
    return () =>
      document.removeEventListener(
        'dropdown:opened',
        handleOtherDropdownOpened,
      );
  }, [instanceId]);

  useEffect(() => {
    if (!draft) return;
    let h = draft.getHours();
    const m = draft.getMinutes();
    const alignedM = Math.round(m / minuteStep) * minuteStep;
    const isPM = h >= 12;
    setAmpm(isPM ? 'PM' : 'AM');
    h = h % 12;
    h = h ? h : 12;
    setHour(h.toString().padStart(2, '0'));
    setMinute(alignedM.toString().padStart(2, '0'));
  }, [draft, minuteStep]);

  const handleOpen = () => {
    if (disabled) return;
    document.dispatchEvent(
      new CustomEvent('dropdown:opened', { detail: { id: instanceId } }),
    );
    setPlacement(calculatePlacement());
    setDraft(selected);
    setShowTimePanel(false);
    setIsOpen(true);
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  };

  const getValidCurrentTime = () => {
    const now = new Date();
    let mins = now.getMinutes();
    let secs = now.getSeconds();

    if (secs > 0 || mins % minuteStep !== 0) {
      const nextMin =
        Math.ceil((mins + (secs > 0 ? 0.1 : 0)) / minuteStep) * minuteStep;
      now.setMinutes(nextMin);
      now.setSeconds(0);
    }

    return {
      currentHour24: now.getHours(),
      currentMinute: now.getMinutes(),
    };
  };

  const buildDateWithTime = (baseDate, nextHour, nextMinute, nextAmpm) => {
    let newHour = parseInt(nextHour, 10);
    let newMinute = parseInt(nextMinute, 10);

    if (nextAmpm === 'PM' && newHour < 12) newHour += 12;
    if (nextAmpm === 'AM' && newHour === 12) newHour = 0;

    const { currentHour24: currH, currentMinute: currM } =
      getValidCurrentTime();

    if (disablePastTime && isToday(baseDate)) {
      if (newHour < currH) {
        newHour = currH;
        newMinute = currM;
      } else if (newHour === currH && newMinute < currM) {
        newMinute = currM;
      }
    }

    const newDate = new Date(baseDate);
    newDate.setHours(newHour);
    newDate.setMinutes(newMinute);
    newDate.setSeconds(0);
    newDate.setMilliseconds(0);
    return newDate;
  };

  const handleTimeChange = (type, value) => {
    if (!draft) return;

    const nextHour = type === 'hour' ? value : hour;
    const nextMinute = type === 'minute' ? value : minute;
    const nextAmpm = type === 'ampm' ? value : ampm;
    setDraft(buildDateWithTime(draft, nextHour, nextMinute, nextAmpm));
  };

  const getHourOptions = () => {
    const shouldDisablePast = disablePastTime && isToday(draft);
    const { currentHour24 } = getValidCurrentTime();
    const options = [];
    for (let i = 1; i <= 12; i++) {
      const str = i.toString().padStart(2, '0');
      let optDisabled = false;
      if (shouldDisablePast) {
        let val24 = i;
        if (ampm === 'PM' && i < 12) val24 += 12;
        if (ampm === 'AM' && i === 12) val24 = 0;
        if (val24 < currentHour24) optDisabled = true;
      }
      options.push({ value: str, label: str, disabled: optDisabled });
    }
    return options;
  };

  const getMinuteOptions = () => {
    const shouldDisablePast = disablePastTime && isToday(draft);
    const { currentHour24, currentMinute } = getValidCurrentTime();
    const options = [];
    let h24 = parseInt(hour, 10);
    if (ampm === 'PM' && h24 < 12) h24 += 12;
    if (ampm === 'AM' && h24 === 12) h24 = 0;

    for (let i = 0; i < 60; i += minuteStep) {
      const str = i.toString().padStart(2, '0');
      let optDisabled = false;
      if (shouldDisablePast && h24 === currentHour24 && i < currentMinute) {
        optDisabled = true;
      }
      options.push({ value: str, label: str, disabled: optDisabled });
    }
    return options;
  };

  const getAmpmOptions = () => {
    const shouldDisablePast = disablePastTime && isToday(draft);
    const { currentHour24 } = getValidCurrentTime();
    let amDisabled = false;
    if (shouldDisablePast && currentHour24 >= 12) {
      amDisabled = true;
    }
    return [
      { value: 'AM', label: 'AM', disabled: amDisabled },
      { value: 'PM', label: 'PM', disabled: false },
    ];
  };

  const handleDateSelect = (date) => {
    if (!date) {
      setDraft(null);
      setShowTimePanel(false);
      if (mode === 'date') onChange(null);
      return;
    }

    const newDate = new Date(date);

    if (mode === 'datetime') {
      setPlacement(calculatePlacement());
      const sameDay =
        draft &&
        draft.getDate() === newDate.getDate() &&
        draft.getMonth() === newDate.getMonth() &&
        draft.getFullYear() === newDate.getFullYear();

      if (sameDay) {
        setShowTimePanel(true);
        return;
      }

      let h24 = draft ? draft.getHours() : 12;
      let m = draft ? draft.getMinutes() : 0;

      if (!draft && isToday(newDate)) {
        const { currentHour24, currentMinute } = getValidCurrentTime();
        h24 = currentHour24;
        m = currentMinute;
      }

      if (disablePastTime && isToday(newDate)) {
        const { currentHour24, currentMinute } = getValidCurrentTime();
        if (
          h24 < currentHour24 ||
          (h24 === currentHour24 && m < currentMinute)
        ) {
          h24 = currentHour24;
          m = currentMinute;
        }
      }

      newDate.setHours(h24);
      newDate.setMinutes(m);
      newDate.setSeconds(0);
      newDate.setMilliseconds(0);
      setDraft(newDate);
      setShowTimePanel(true);
      return;
    }

    newDate.setHours(0, 0, 0, 0);
    setDraft(newDate);
    onChange(newDate);
    setIsOpen(false);
  };

  const handleDone = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (draft) onChange(draft);
    setIsOpen(false);
  };

  const handleClickOutside = (event) => {
    if (isPickerExemptTarget(event.target)) {
      event.preventDefault();
      return;
    }
    setIsOpen(false);
  };

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const todayEnd = new Date();
  todayEnd.setHours(23, 59, 59, 999);

  let computedMinDate = minDate;
  if (!allowPastDates) {
    computedMinDate =
      computedMinDate && computedMinDate > todayStart
        ? computedMinDate
        : todayStart;
  }

  let computedMaxDate = maxDate;
  if (!allowFutureDates) {
    computedMaxDate =
      computedMaxDate && computedMaxDate < todayEnd
        ? computedMaxDate
        : todayEnd;
  }

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();

  const minYear = computedMinDate
    ? computedMinDate.getFullYear()
    : !allowPastDates
      ? currentYear
      : currentYear - 50;

  const maxYear = computedMaxDate
    ? computedMaxDate.getFullYear()
    : !allowFutureDates
      ? currentYear
      : currentYear + 25;

  const displayValue = selected ? format(selected, computedDateFormat) : '';

  return (
    <div ref={containerRef} className="w-full relative">
      <DatePicker
        selected={draft || selected}
        onChange={handleDateSelect}
        dateFormat={computedDateFormat}
        placeholderText={placeholderText}
        disabled={disabled}
        className={className}
        wrapperClassName="w-full block"
        shouldCloseOnSelect={mode === 'date'}
        allowSameDay={mode === 'datetime'}
        minDate={computedMinDate || undefined}
        maxDate={computedMaxDate || undefined}
        onChangeRaw={(e) => e.preventDefault()}
        portalId={PORTAL_ID}
        popperClassName="!z-[999999]"
        popperPlacement={placement}
        popperProps={POPPER_PROPS}
        enableTabLoop={false}
        onInputClick={handleOpen}
        onClickOutside={handleClickOutside}
        open={isOpen}
        renderCustomHeader={({
          date,
          changeYear,
          changeMonth,
          decreaseMonth,
          increaseMonth,
          prevMonthButtonDisabled,
          nextMonthButtonDisabled,
        }) => {
          const headerYear = date.getFullYear();
          const headerMonth = date.getMonth();
          const effectiveMinYear = Math.min(minYear, headerYear);
          const effectiveMaxYear = Math.max(maxYear, headerYear);
          const years = [];
          for (let y = effectiveMinYear; y <= effectiveMaxYear; y++) {
            years.push(y);
          }

          return (
            <div className="flex items-center justify-between px-2.5 sm:px-3.5 pb-2">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  decreaseMonth();
                }}
                disabled={prevMonthButtonDisabled}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-[#1F2241] disabled:cursor-not-allowed transition-all cursor-pointer"
                aria-label="Previous month"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5L8.25 12l7.5-7.5"
                  />
                </svg>
              </button>

              <div className="flex items-center gap-2">
                {/* Month Label */}
                <span className="text-sm font-bold text-[#1F2241]">
                  {MONTH_NAMES[headerMonth]}
                </span>

                {/* Year Selector */}
                <YearSelectDropdown
                  value={headerYear}
                  options={years}
                  onChange={(newYear) => {
                    changeYear(newYear);
                    if (
                      !allowPastDates &&
                      newYear === currentYear &&
                      headerMonth < currentMonth
                    ) {
                      changeMonth(currentMonth);
                    }
                  }}
                />
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  increaseMonth();
                }}
                disabled={nextMonthButtonDisabled}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-[#1F2241] hover:bg-[#EBF4EA] hover:text-[#50AB3D] disabled:opacity-20 disabled:hover:bg-transparent disabled:hover:text-[#1F2241] disabled:cursor-not-allowed transition-all cursor-pointer"
                aria-label="Next month"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 4.5l7.5 7.5-7.5 7.5"
                  />
                </svg>
              </button>
            </div>
          );
        }}
        customInput={
          <DateTimeTextInput
            displayValue={displayValue}
            className={className}
            placeholder={placeholderText}
            disabled={disabled}
          />
        }
      >
        {showTimePanel && draft && mode === 'datetime' && (
          <div className="flex flex-col gap-2.5 p-3 border-t border-[#EBF4EA] bg-[#ffffff] text-[#1F2241]">
            <div className="text-[11px] font-bold text-center text-[#868686] uppercase tracking-wider">
              Select Time
            </div>
            <div className="flex gap-2 justify-center items-center">
              <TimeSelectDropdown
                value={hour}
                options={getHourOptions()}
                onChange={(val) => handleTimeChange('hour', val)}
                width="w-16"
              />
              <span className="font-bold text-[#868686]">:</span>
              <TimeSelectDropdown
                value={minute}
                options={getMinuteOptions()}
                onChange={(val) => handleTimeChange('minute', val)}
                width="w-16"
              />
              <TimeSelectDropdown
                value={ampm}
                options={getAmpmOptions()}
                onChange={(val) => handleTimeChange('ampm', val)}
                width="w-20"
              />
            </div>

            <div className="flex justify-end mt-1 pt-2.5 border-t border-[#EBF4EA]">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleDone}
                className="px-4 py-1.5 rounded-lg bg-[#50AB3D] hover:bg-[#439632] active:scale-95 text-white text-xs uppercase tracking-wider font-bold shadow-sm transition-all cursor-pointer"
              >
                DONE
              </button>
            </div>
          </div>
        )}
      </DatePicker>
    </div>
  );
}

export const DateTimePicker = CommonDatePicker;
